import { useMount } from '@tiger/hooks';
import { Table as AndTable } from 'antd';
import { useMemo, useState } from 'react';
import { ColumnProps } from 'antd/es/table';
import dayjs from 'dayjs';
import ErrorBox from 'components/ErrorBox';
import Icon from 'components/Icon';
import { ReactComponent as ArrowDownIcon } from 'static/images/icons/arrow-down.svg';
import security from 'stores/security';
import { Source } from 'types/funding';
import API from 'utils/API';
import classlist from 'utils/classlist';
import { formatMoney } from 'utils/format';
import http from 'utils/http';
import StatusTag from '../StatusTag';

import './style.scss';

type OperationType = Source;

enum RecordType {
    Deposit = 1, // 入金
    Withdraw = 3, // 出金
    WithdrawFee = 20, // 出金费用
    WithdrawReturn, // 出金退款
    WithdrawFailReturn, // 出金失败-退款
    WithdrawFeeReturn // 出金费用-退款
}

// 字段参考
// https://git.tigerbrokers.net/alpha/site-api-docs/-/blob/master/api/funding-site/funds.md
interface DepositWithdrawRecord {
    id: number; // 序号
    refId: string; // 关联业务ID
    type: RecordType; // 资金类型
    typeDesc: string; // 类型描述
    currency: string; // 币种
    amount: number; // 金额
    createdAt: number; // 创建时间
    updatedAt: number; // 最近修改时间
    businessDate: string; // 业务日期
    status: number; // US 环境返回的transfer 状态
    statusDesc: string; // us环境返回的状态描述
    completedStatus: boolean; // 是否是终态
}

const RecordsTable: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const [dataSource, setDataSource] = useState<DepositWithdrawRecord[]>([]);
    const [currentType, setCurrentType] = useState<OperationType | undefined>();
    const isIRA = security.isIRAAccount;

    useMount(() => {
        getListData();
    });

    const getListData = async (type?: OperationType) => {
        setLoading(true);
        setError(null);

        try {
            const { data } = await http.get(API.TRADE.history.getTransferRecord(), {
                params: {
                    direction: type,
                    account_id: security.currentAccountId
                }
            });

            setDataSource(data);
        } catch (error) {
            setError(error);
        }

        setLoading(false);
    };

    const column: Array<ColumnProps<DepositWithdrawRecord>> = useMemo(() => {
        const filterOptions: Array<{ text: string; value?: OperationType }> = [
            {
                text: __('All')
            },
            {
                text: isIRA ? __('Contribution') : __('Deposit'),
                value: 'deposit'
            },
            {
                text: isIRA ? __('Distribution') : __('Withdraw'),
                value: 'withdraw'
            }
        ];

        const columns: typeof column = [
            {
                title: __('Date'),
                dataIndex: 'createdAt',
                render: value => (value ? dayjs(value).format('YYYY/MM/DD HH:mm:ss') : '--'),
                className: 'funding-records-table-cell'
            },
            {
                title: __('Type'),
                dataIndex: 'typeDesc',
                filterIcon: <Icon title="type" className="icon-arrow-down" component={ArrowDownIcon} />,
                filterDropdown: ({ close }) => (
                    <ul className="funding-records-table-filter-options">
                        {filterOptions.map(({ text, value }) => (
                            <li
                                key={text}
                                className={classlist('filter-type', {
                                    active: currentType === value
                                })}
                                onClick={() => {
                                    getListData(value);
                                    setCurrentType(value);
                                    close();
                                }}>
                                {text}
                            </li>
                        ))}
                    </ul>
                ),
                className: 'funding-records-table-cell',
                render: (value, record) => {
                    if (isIRA) {
                        if (record.type === RecordType.Deposit) {
                            return __('Contribution');
                        }

                        if (record.type === RecordType.Withdraw) {
                            return __('Distribution');
                        }

                        return record.typeDesc;
                    }

                    if (record.type === RecordType.Deposit) {
                        return __('Deposit');
                    }

                    if (record.type === RecordType.Withdraw) {
                        return __('Withdraw');
                    }

                    return record.typeDesc;
                }
            },
            {
                title: __('Amount'),
                dataIndex: 'amount',
                render: (value, records) => (
                    <>
                        {formatMoney(Number(value).toFixed(2))} {records.currency}
                    </>
                ),
                align: 'right',
                className: 'funding-records-table-cell'
            },
            {
                title: __('Status'),
                dataIndex: 'status',
                render: value => <StatusTag status={value} />,
                align: 'right',
                className: 'funding-records-table-cell'
            }
        ];

        if (security.isIRAAccount) {
            return [
                ...columns,
                {
                    title: __('Tax Year'),
                    dataIndex: 'taxYear',
                    align: 'right',
                    className: 'funding-records-table-cell'
                }
            ];
        }

        return columns;
    }, [currentType, isIRA]);

    const renderMain = () => {
        if (error) {
            return <ErrorBox error={error} onClick={() => getListData(currentType)} />;
        }

        return (
            <AndTable
                className="funding-records-table"
                rowKey="id"
                loading={loading}
                tableLayout="fixed"
                columns={column}
                dataSource={dataSource}
                bordered={false}
                scroll={{ x: 500 }}
                pagination={{
                    position: ['bottomCenter']
                }}
            />
        );
    };

    return <div className="funding-records-table-root">{renderMain()}</div>;
};

export default RecordsTable;
